import React, { useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Bell, Gavel, Mail, Smartphone, Settings, Check, X } from 'lucide-react';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'bid' | 'auction' | 'system' | 'win';
  timestamp: string;
  read: boolean;
  priority: 'low' | 'medium' | 'high';
}

const NotificationsTab: React.FC = () => {
  const [notifications] = useState<Notification[]>([
    {
      id: '1',
      title: 'Bid Outbid',
      message: 'Your bid on "Sunset Over the Everglades" has been outbid. Current bid: $1,250',
      type: 'bid',
      timestamp: '2024-01-10T14:30:00Z',
      read: false,
      priority: 'high'
    },
    {
      id: '2',
      title: 'Auction Reminder',
      message: 'The "Florida Masters Collection" auction starts in 1 hour',
      type: 'auction',
      timestamp: '2024-01-10T12:00:00Z',
      read: true,
      priority: 'medium'
    },
    {
      id: '3',
      title: 'Congratulations!',
      message: 'You won Lot #45 "Morning Glory" for $850',
      type: 'win',
      timestamp: '2024-01-09T18:45:00Z',
      read: false,
      priority: 'high'
    }
  ]);

  const [settings, setSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    bidAlerts: true,
    auctionReminders: true,
    winNotifications: true
  });

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'bid': return <Gavel className="h-4 w-4" />;
      case 'auction': return <Bell className="h-4 w-4" />;
      case 'win': return <Check className="h-4 w-4" />;
      case 'system': return <Settings className="h-4 w-4" />;
      default: return <Bell className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'bid': return 'bg-orange-100 text-orange-800';
      case 'auction': return 'bg-blue-100 text-blue-800';
      case 'win': return 'bg-green-100 text-green-800';
      case 'system': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'border-l-red-500';
      case 'medium': return 'border-l-yellow-500';
      case 'low': return 'border-l-green-500';
      default: return 'border-l-gray-500';
    }
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-6 space-y-6">
        {/* Notification Settings */}
        <Card className="bg-white/10 backdrop-blur-sm border-white/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Settings className="h-5 w-5" />
              Notification Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-white" />
                <span className="text-white">Email Notifications</span>
              </div>
              <Switch 
                checked={settings.emailNotifications}
                onCheckedChange={(checked) => setSettings({...settings, emailNotifications: checked})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Smartphone className="h-4 w-4 text-white" />
                <span className="text-white">SMS Notifications</span>
              </div>
              <Switch 
                checked={settings.smsNotifications}
                onCheckedChange={(checked) => setSettings({...settings, smsNotifications: checked})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Gavel className="h-4 w-4 text-white" />
                <span className="text-white">Bid Alerts</span>
              </div>
              <Switch 
                checked={settings.bidAlerts}
                onCheckedChange={(checked) => setSettings({...settings, bidAlerts: checked})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-white" />
                <span className="text-white">Auction Reminders</span>
              </div>
              <Switch 
                checked={settings.auctionReminders}
                onCheckedChange={(checked) => setSettings({...settings, auctionReminders: checked})}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-white" />
                <span className="text-white">Win Notifications</span>
              </div>
              <Switch 
                checked={settings.winNotifications}
                onCheckedChange={(checked) => setSettings({...settings, winNotifications: checked})}
              />
            </div>
          </CardContent>
        </Card>

        {/* Recent Notifications */}
        <Card className="bg-white/10 backdrop-blur-sm border-white/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Bell className="h-5 w-5" />
              Recent Notifications
            </CardTitle>
          </CardHeader>
          <CardContent>
            {notifications.length === 0 ? (
              <div className="text-center py-8 text-white/70">
                <Bell className="h-12 w-12 mx-auto mb-4 text-white/30" />
                <p>No notifications yet</p>
                <p className="text-sm mt-2">You'll receive updates about bids and auctions here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {notifications.map((notification) => (
                  <div 
                    key={notification.id} 
                    className={`p-4 rounded-lg border-l-4 transition-colors ${getPriorityColor(notification.priority)} ${
                      notification.read 
                        ? 'bg-white/5 border-white/10' 
                        : 'bg-white/10 border-white/20'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`p-2 rounded-lg ${getTypeColor(notification.type)} flex items-center justify-center`}>
                          {getTypeIcon(notification.type)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-white">{notification.title}</h4>
                            {!notification.read && (
                              <div className="w-2 h-2 bg-blue-500 rounded-full" />
                            )}
                          </div>
                          <p className="text-white/80 text-sm mb-2">{notification.message}</p>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className={getTypeColor(notification.type)}>
                              {notification.type}
                            </Badge>
                            <span className="text-xs text-white/60">
                              {new Date(notification.timestamp).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1 ml-2">
                        <Button size="sm" variant="ghost" className="p-1 h-auto text-white/60 hover:text-white hover:bg-white/10">
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="p-1 h-auto text-white/60 hover:text-white hover:bg-white/10">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </ScrollArea>
  );
};

export default NotificationsTab;